import axios from 'axios';

class ShopifyAuthService {
  constructor() {
    this.accessToken = null;
    this.shop = null;
  }

  async initializeAuth(shopName) {
    const apiKey = process.env.REACT_APP_SHOPIFY_API_KEY;
    
    if (!apiKey || apiKey === 'your_shopify_api_key_here') {
      throw new Error('Shopify API Key not configured. Please set REACT_APP_SHOPIFY_API_KEY in your .env file.');
    }
    
    if (!shopName) {
      throw new Error('Shop name is required for Shopify authentication');
    }

    // Remove .myshopify.com if included
    const cleanShopName = shopName.replace('.myshopify.com', '');
    this.shop = `${cleanShopName}.myshopify.com`;
    
    const redirectUri = `${window.location.origin}/auth/shopify/callback`;
    const state = this.generateState();
    
    // Store state and shop for callback verification
    localStorage.setItem('shopify_oauth_state', state);
    localStorage.setItem('shopify_shop_name', this.shop);
    
    const scopes = [
      'read_products',
      'write_products',
      'read_orders',
      'write_orders',
      'read_customers',
      'write_customers',
      'read_inventory',
      'write_inventory',
      'read_analytics',
      'read_marketing_events',
      'write_marketing_events',
      'read_content',
      'write_content',
      'read_themes',
      'write_themes',
      'read_script_tags',
      'write_script_tags',
      'read_fulfillments',
      'write_fulfillments'
    ].join(',');

    const authUrl = `https://${this.shop}/admin/oauth/authorize?` +
      `client_id=${apiKey}&` +
      `scope=${encodeURIComponent(scopes)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `state=${state}`;

    return {
      authUrl,
      shop: this.shop,
      apiKey,
      redirectUri,
      state
    };
  }

  generateState() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  async handleAuthCallback(code, state, shop) {
    try {
      const storedState = localStorage.getItem('shopify_oauth_state');
      const storedShop = localStorage.getItem('shopify_shop_name');

      if (state !== storedState) {
        throw new Error('Invalid state parameter');
      }

      if (shop !== storedShop) {
        throw new Error('Shop mismatch');
      }

      const apiKey = process.env.REACT_APP_SHOPIFY_API_KEY;
      const apiSecret = process.env.REACT_APP_SHOPIFY_API_SECRET;

      const tokenResponse = await axios.post(`https://${shop}/admin/oauth/access_token`, {
        client_id: apiKey,
        client_secret: apiSecret,
        code: code
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      this.accessToken = tokenResponse.data.access_token;
      this.shop = shop;

      // Store tokens securely
      const tokens = {
        access_token: this.accessToken,
        scope: tokenResponse.data.scope,
        shop: shop
      };

      localStorage.setItem('shopify_tokens', JSON.stringify(tokens));
      localStorage.removeItem('shopify_oauth_state');
      localStorage.removeItem('shopify_shop_name');

      return tokens;
    } catch (error) {
      console.error('Shopify Auth callback error:', error);
      throw error;
    }
  }

  async getShopInfo() {
    try {
      if (!this.accessToken) {
        const storedTokens = localStorage.getItem('shopify_tokens');
        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);
          this.accessToken = tokens.access_token;
          this.shop = tokens.shop;
        } else {
          throw new Error('Shopify access token not found');
        }
      }

      const response = await axios.get(`https://${this.shop}/admin/api/2023-10/shop.json`, {
        headers: {
          'X-Shopify-Access-Token': this.accessToken
        }
      });

      return this.formatShopData(response.data.shop);
    } catch (error) {
      console.error('Error fetching Shopify shop info:', error);
      throw error;
    }
  }

  formatShopData(shop) {
    return {
      id: shop.id,
      name: shop.name,
      email: shop.email,
      domain: shop.domain,
      myshopifyDomain: shop.myshopify_domain,
      description: shop.description,
      phone: shop.phone,
      address1: shop.address1,
      address2: shop.address2,
      city: shop.city,
      province: shop.province,
      country: shop.country,
      zip: shop.zip,
      currency: shop.currency,
      timezone: shop.iana_timezone,
      planName: shop.plan_name,
      planDisplayName: shop.plan_display_name,
      shopOwner: shop.shop_owner,
      latitude: shop.latitude,
      longitude: shop.longitude,
      primaryLocale: shop.primary_locale,
      createdAt: shop.created_at,
      updatedAt: shop.updated_at,
      source: 'shopify'
    };
  }

  async getProducts(limit = 50, fields = null) {
    try {
      if (!this.accessToken) {
        const storedTokens = localStorage.getItem('shopify_tokens');
        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);
          this.accessToken = tokens.access_token;
          this.shop = tokens.shop;
        } else {
          throw new Error('Shopify access token not found');
        }
      }

      const params = { limit };
      if (fields) {
        params.fields = fields;
      }

      const response = await axios.get(`https://${this.shop}/admin/api/2023-10/products.json`, {
        headers: {
          'X-Shopify-Access-Token': this.accessToken
        },
        params
      });

      return response.data.products || [];
    } catch (error) {
      console.error('Error fetching Shopify products:', error);
      return [];
    }
  }

  async getOrders(status = 'any', limit = 50) {
    try {
      if (!this.accessToken) {
        throw new Error('Shopify access token not found');
      }

      const response = await axios.get(`https://${this.shop}/admin/api/2023-10/orders.json`, {
        headers: {
          'X-Shopify-Access-Token': this.accessToken
        },
        params: {
          status,
          limit
        }
      });

      return response.data.orders || [];
    } catch (error) {
      console.error('Error fetching Shopify orders:', error);
      return [];
    }
  }

  async getCustomers(limit = 50) {
    try {
      if (!this.accessToken) {
        throw new Error('Shopify access token not found');
      }

      const response = await axios.get(`https://${this.shop}/admin/api/2023-10/customers.json`, {
        headers: {
          'X-Shopify-Access-Token': this.accessToken
        },
        params: { limit }
      });

      return response.data.customers || [];
    } catch (error) {
      console.error('Error fetching Shopify customers:', error);
      return [];
    }
  }

  async getAnalytics() {
    try {
      if (!this.accessToken) {
        throw new Error('Shopify access token not found');
      }

      // Get shop analytics for the last 30 days
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      const [ordersResponse, customersResponse, productsResponse] = await Promise.all([
        this.getOrders('any', 250),
        this.getCustomers(250),
        this.getProducts(250)
      ]);

      // Calculate analytics from the data
      const recentOrders = ordersResponse.filter(order => 
        new Date(order.created_at) >= startDate
      );

      const totalSales = recentOrders.reduce((sum, order) => 
        sum + parseFloat(order.total_price), 0
      );

      const avgOrderValue = recentOrders.length > 0 ? 
        totalSales / recentOrders.length : 0;

      return {
        totalSales,
        totalOrders: recentOrders.length,
        avgOrderValue,
        totalCustomers: customersResponse.length,
        totalProducts: productsResponse.length,
        period: '30 days'
      };
    } catch (error) {
      console.error('Error fetching Shopify analytics:', error);
      return {};
    }
  }

  async createProduct(productData) {
    try {
      if (!this.accessToken) {
        throw new Error('Shopify access token not found');
      }

      const response = await axios.post(`https://${this.shop}/admin/api/2023-10/products.json`, 
        { product: productData },
        {
          headers: {
            'X-Shopify-Access-Token': this.accessToken,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data.product;
    } catch (error) {
      console.error('Error creating Shopify product:', error);
      throw error;
    }
  }

  async updateInventory(inventoryItemId, quantity) {
    try {
      if (!this.accessToken) {
        throw new Error('Shopify access token not found');
      }

      // First get the inventory level ID
      const locationsResponse = await axios.get(`https://${this.shop}/admin/api/2023-10/locations.json`, {
        headers: {
          'X-Shopify-Access-Token': this.accessToken
        }
      });

      const primaryLocation = locationsResponse.data.locations[0];

      const response = await axios.post(`https://${this.shop}/admin/api/2023-10/inventory_levels/set.json`, {
        location_id: primaryLocation.id,
        inventory_item_id: inventoryItemId,
        available: quantity
      }, {
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        }
      });

      return response.data.inventory_level;
    } catch (error) {
      console.error('Error updating Shopify inventory:', error);
      throw error;
    }
  }

  clearTokens() {
    localStorage.removeItem('shopify_tokens');
    localStorage.removeItem('shopify_oauth_state');
    localStorage.removeItem('shopify_shop_name');
    this.accessToken = null;
    this.shop = null;
  }

  isAuthenticated() {
    const tokens = localStorage.getItem('shopify_tokens');
    return !!tokens;
  }

  getShopDomain() {
    const tokens = localStorage.getItem('shopify_tokens');
    if (tokens) {
      const parsed = JSON.parse(tokens);
      return parsed.shop;
    }
    return null;
  }
}

export default ShopifyAuthService;